<template>
  <AccountingImportDialog
    v-model:visibility="reanalyzeDialogVisible"
    :reanalyze-import-id="reanalyzeImportId"
    @imported="handleReanalyzeImported" />

  <AccountingImportEditDialog
    v-if="editDialogVisible && selectedImport"
    v-model:visible="editDialogVisible"
    :import-record="selectedImport"
    :available-tags="availableTags"
    @updated="handleImportUpdated" />

  <AccountingImportExportDialog
    v-model:visibility="exportDialogVisible"
    v-if="accountingImportId"
    :accounting-import-id="accountingImportId"
    :target-currency-code="targetCurrencyCode"
    :filters="exportFilters" />

  <main class="accounting-details-grid">
    <div class="header-card">
      <el-card shadow="always" :body-style="{ height: '100%', width: '100%' }">
        <!-- Base Infos -->
        <div
          v-if="loadingDetail"
          class="flex h-full items-center justify-center">
          <span>{{ i18n.t('loading') }}</span>
        </div>
        <div
          v-else-if="accountingImport"
          class="flex h-full items-center justify-between gap-4 pr-12">
          <div class="flex flex-wrap items-center gap-4 gap-y-2">
            <h2 class="text-xl font-semibold">
              {{ displayName }}
            </h2>

            <SmDropdown type="panel" placement="right" dropdown-class="w-48">
              <template #trigger>
                <TextMultiline :show-tool-tip="false">
                  <div class="item-center group/button flex w-full gap-1">
                    <SquarePen
                      :size="16"
                      class="mb-[2px] stroke-contrast-muted hover:stroke-contrast"
                      style="cursor: pointer" />
                  </div>
                </TextMultiline>
              </template>
              <SmDropdownItem
                :icon="PencilLine"
                @click="handleApplicationEditButton">
                {{ i18n.t('general.edit') }}
              </SmDropdownItem>
              <SmDropdownItem
                :icon="Download"
                @click="accountingImport && downloadImport(accountingImport)">
                {{ i18n.t('download') }}
              </SmDropdownItem>
              <SmDropdownItem
                v-if="accountingImport"
                :icon="Loader"
                @click="openReanalyze()">
                {{
                  accountingImport.status === AccountingImportStatus.UPLOADED
                    ? i18n.t('accountingExport.startProcessing')
                    : i18n.t('accountingExport.reanalyze')
                }}
              </SmDropdownItem>
              <SmDropdownItem
                :icon="Trash"
                hover-color="var(--sm-magenta)"
                @click="accountingImport && deleteImport(accountingImport._id)">
                {{ i18n.t('delete') }}
              </SmDropdownItem>
            </SmDropdown>

            <div v-if="accountingImport.description" class="basis-full">
              <TextMultiline class="text-contrast-muted">
                {{ accountingImport.description }}
              </TextMultiline>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-8">
            <StatsElement
              v-for="stat in stats"
              :key="stat.displayI18nKey"
              :icon="stat.icon"
              :color="stat.color"
              :display-i18n-key="stat.displayI18nKey"
              :display-number="stat.displayNumber"
              :is-price="stat.isPrice ?? false" />
          </div>
        </div>
        <div v-else class="flex h-full items-center justify-center">
          <p>{{ i18n.t('noData') }}</p>
        </div>
      </el-card>
    </div>

    <div class="detail-card">
      <el-card
        shadow="always"
        class="h-full"
        body-class="flex flex-col gap-4"
        :body-style="{ height: '100%', width: '100%' }">
        <div
          v-if="showTable"
          class="table-controls-container flex w-full flex-wrap items-center gap-3">
          <div class="min-w-[150px] max-w-[300px] flex-1">
            <SmInput
              v-model="searchTerm"
              outline
              size="medium"
              :label="i18n.t('search')"
              @input="handleSearchInput" />
          </div>

          <div style="display: flex; align-items: center; gap: 10px">
            <SmDropdown
              :close-on-click-inside="false"
              dropdown-class="w-[28rem]">
              <template #trigger>
                <SmButton outline size="small">
                  <div class="flex items-center gap-2">
                    <Filter :size="18" />
                    <span>{{ i18n.t('filter') }}</span>
                    <span v-if="filterCount"> ({{ filterCount }}) </span>
                  </div>
                </SmButton>
              </template>

              <div class="space-y-4 p-4">
                <div v-if="aiPredictionEnabled" class="flex flex-col gap-1">
                  <label class="text-sm text-contrast-muted">
                    {{ i18n.t('source') }}
                  </label>
                  <SmSelect
                    v-model:selection="sourceSelection"
                    class="w-full"
                    :options="sourceOptions" />
                </div>

                <div v-if="aiPredictionEnabled" class="flex flex-col gap-1">
                  <label class="text-sm text-contrast-muted">
                    {{ i18n.t('aiThreshold') }}
                  </label>
                  <SmSlider
                    v-model.number="thresholdFilter"
                    :min-value="0"
                    :max-value="100"
                    :show-legend="true"
                    :disabled="!aiPredictionEnabled" />
                </div>

                <div class="flex flex-col gap-1">
                  <label class="text-sm text-contrast-muted">
                    {{ i18n.t('blacklisted') }}
                  </label>
                  <div
                    class="rounded-md dark:bg-transparent flex items-center gap-2 py-2 text-sm">
                    <SmSwitch v-model="blacklistedSelection" />
                    <span class="text-contrast-muted">
                      {{
                        blacklistedSelection
                          ? i18n.t('blacklisted')
                          : i18n.t('notBlacklisted')
                      }}
                    </span>
                  </div>
                </div>
              </div>
            </SmDropdown>
          </div>

          <div class="ml-auto flex items-center gap-2">
            <SmButton size="small" @click="openExportDialog">
              <div class="flex items-center gap-2">
                <Download :size="18" />
                <span>{{ i18n.t('export') }}</span>
              </div>
            </SmButton>
          </div>
        </div>

        <template v-if="showTable">
          <div class="flex-1" style="height: calc(100% - 34px - 1rem)">
            <AccountingDetailTable
              :rows="filteredSummaryRows"
              :loading="tableLoading"
              :total-count="allSoftwareSummary.length"
              :details-state="softwareDetails"
              :on-expand-row="handleExpandRow"
              :target-currency-code="targetCurrencyCode"
              :ai-prediction-enabled="aiPredictionEnabled" />
          </div>
        </template>
        <template v-else-if="emptyStateMeta">
          <EmptyState
            :icon="emptyStateMeta.icon"
            :title="emptyStateMeta.title"
            :text="emptyStateMeta.text"
            :color="emptyStateMeta.color"
            :show-create-btn="emptyStateActions.showCreateBtn"
            :show-docs-btn="emptyStateActions.showDocsBtn"
            :create-label="emptyStateActions.createLabel"
            :docs-label="emptyStateActions.docsLabel"
            @create="handleEmptyStateCreate"
            @show-docs="handleEmptyStateShowDocs" />
        </template>
      </el-card>
    </div>
  </main>
</template>

<script setup lang="ts">
  import { onMounted, ref, watch, computed } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import { useDebounceFn } from '@vueuse/core'

  import {
    AccountingImportOut,
    AccountingImportStatus,
    AccountingImportStatisticsSoftwareSummary,
    AccountingImportRow,
    AccountingService,
    OpenAPI,
    Tag,
    TagsService,
  } from '@/client'
  import { sendToast } from '@/components/sm/SmNotification'
  import { getCorrectedPrice } from '@/utils/formatter/price'
  import AccountingDetailTable from '@/components/accounting/AccountingDetailTable.vue'
  import AccountingImportExportDialog from '@/components/accounting/AccountingImportExportDialog.vue'
  import SmDropdown from '@/components/sm/SmDropdown.vue'
  import SmButton from '@/components/sm/SmButton.vue'
  import SmSwitch from '@/components/sm/SmSwitch.vue'
  import SmInput from '@/components/sm/SmInput.vue'
  import SmSelect from '@/components/sm/SmSelect.vue'
  import SmSlider from '@/components/sm/SmSlider.vue'
  import {
    SquarePen,
    Download,
    Trash,
    Wallet,
    SearchCheck,
    PencilLine,
    Clock,
    AlertTriangle,
    Filter,
    CircleDot,
    Brain,
    Loader,
    SearchX,
  } from 'lucide-vue-next'
  import StatsElement from '@/components/shared/StatsElement.vue'
  import EmptyState from '@/components/shared/EmptyState.vue'
  import AccountingImportEditDialog from '@/components/accounting/AccountingImportEditDialog.vue'
  import AccountingImportDialog from '@/components/discovery/AccountingImportDialog.vue'
  import { usePreferenceStore } from '@/stores/preferenceStore'

  const route = useRoute()
  const router = useRouter()
  const i18n = useI18n()

  // ════════════════════════════════════════════════════════════════════════════
  //  State
  // ════════════════════════════════════════════════════════════════════════════
  const accountingImport = ref<AccountingImportOut | null>(null)
  const loadingDetail = ref(false)
  const rowsLoading = ref(false)
  const delayedLoading = ref(false)
  let loadingTimer: number | undefined
  const reanalyzeDialogVisible = ref(false)
  const reanalyzeImportId = ref<string | null>(null)
  const softwareSummary = ref<AccountingImportStatisticsSoftwareSummary[]>([])
  const allSoftwareSummary = ref<AccountingImportStatisticsSoftwareSummary[]>(
    []
  )
  const filteredSummaryRows = ref<AccountingImportStatisticsSoftwareSummary[]>(
    []
  )
  const softwareDetails = ref<
    Record<
      string,
      { loading: boolean; loaded: boolean; rows: AccountingImportRow[] }
    >
  >({})
  const totalRowsCount = ref(0)
  const totalSaasSpends = ref<number | null>(null)
  const editDialogVisible = ref(false)
  const selectedImport = ref<AccountingImportOut | null>(null)
  const availableTags = ref<Tag[]>([])
  const exportDialogVisible = ref(false)
  const preferenceStore = usePreferenceStore()
  const searchTerm = ref('')
  const sourceOptions = computed(() => [
    { value: 'all', label: i18n.t('all') },
    { value: 'ai', label: i18n.t('aiDetected') },
    { value: 'rulebased', label: i18n.t('rulebased') },
  ])
  const sourceSelection = ref([sourceOptions.value[0]])
  const thresholdFilter = ref<number | null>(80)
  const blacklistedSelection = ref(false)
  const sourceValue = computed(() => sourceSelection.value?.[0]?.value ?? 'all')
  const blacklistedValue = computed(() =>
    blacklistedSelection.value ? 'blacklisted' : 'not_blacklisted'
  )
  const targetCurrencyCode = computed(() => {
    const uploadCurrency = accountingImport.value?.config?.currency_code
    return uploadCurrency ?? preferenceStore.preferences?.currency_code
  })
  const initializing = ref(true)
  const lastSummaryCurrency = ref<string | null>(null)
  const aiPredictionEnabled = computed(() => {
    return accountingImport.value?.config?.enable_ai_prediction !== false
  })
  const fetchSummaryDebounced = useDebounceFn(() => {
    fetchImportStatisticsSummary()
  }, 400)
  // ════════════════════════════════════════════════════════════════════════════
  //  Computed
  // ════════════════════════════════════════════════════════════════════════════

  const totalSaasSpendsFormatted = computed(() => {
    if (totalSaasSpends.value === null || totalSaasSpends.value === undefined)
      return null
    const formatted = getCorrectedPrice(
      totalSaasSpends.value,
      targetCurrencyCode.value ?? undefined
    )
    return formatted ?? totalSaasSpends.value.toString()
  })

  const thresholdActive = computed(() => {
    return (
      aiPredictionEnabled.value &&
      thresholdFilter.value !== null &&
      !Number.isNaN(thresholdFilter.value) &&
      thresholdFilter.value > 0
    )
  })

  const filterCount = computed(() => {
    let count = 0
    if (searchTerm.value.trim()) count += 1
    if (aiPredictionEnabled.value && sourceValue.value !== 'all') count += 1
    if (aiPredictionEnabled.value && thresholdActive.value) count += 1
    return count
  })

  function applyFilters() {
    const term = searchTerm.value.trim().toLowerCase()
    if (!term) {
      filteredSummaryRows.value = [...softwareSummary.value]
      return
    }
    filteredSummaryRows.value = softwareSummary.value.filter((item) =>
      item.software_name.toLowerCase().includes(term)
    )
  }

  function handleSearchInput() {
    applyFilters()
    fetchSummaryDebounced()
  }

  watch(
    sourceSelection,
    () => {
      fetchSummaryDebounced()
    },
    { deep: true }
  )

  watch(blacklistedSelection, () => {
    fetchSummaryDebounced()
  })

  watch(thresholdFilter, () => {
    fetchSummaryDebounced()
  })

  const stats = computed(() => [
    ...(showTable.value
      ? [
          {
            displayI18nKey: 'rowsDetected',
            displayNumber: allSoftwareSummary.value.length || null,
            icon: SearchCheck,
            color: 'purple',
          },
          {
            displayI18nKey: 'detectedVolume',
            displayNumber: totalSaasSpendsFormatted.value || null,
            icon: Wallet,
            color: 'blue',
            isPrice: false,
          },
        ]
      : []),
  ])

  const tableLoading = computed(() => {
    if (!rowsLoading.value) return false
    if (!softwareSummary.value.length) return true
    return delayedLoading.value
  })

  const accountingImportId = computed(function getImportId() {
    return route.params.id as string
  })

  const displayName = computed(() => {
    if (!accountingImport.value) return ''
    return accountingImport.value.name || accountingImport.value.filename
  })

  const importStatus = computed(() => accountingImport.value?.status)
  const noApplicationsDetected = computed(() => {
    return (
      importStatus.value === AccountingImportStatus.DONE &&
      !rowsLoading.value &&
      allSoftwareSummary.value.length === 0
    )
  })

  const showTable = computed(() => {
    return (
      importStatus.value === AccountingImportStatus.DONE &&
      (rowsLoading.value || allSoftwareSummary.value.length > 0)
    )
  })
  const emptyStateMeta = computed(() => {
    if (rowsLoading.value) return null
    switch (importStatus.value) {
      case AccountingImportStatus.UPLOADED:
        return {
          icon: CircleDot,
          color: 'warning' as const,
          title: i18n.t('accountingImport.status.uploaded'),
          text: i18n.t('accountingImport.statusProcessingText'),
        }
      case AccountingImportStatus.IN_PROGRESS:
        return {
          icon: Clock,
          color: 'warning' as const,
          title: i18n.t('accountingImport.status.in_progress'),
          text: i18n.t('accountingImport.statusProcessingText'),
        }
      case AccountingImportStatus.AI_PREDICTION_IN_PROGRESS:
        return {
          icon: Brain,
          color: 'warning' as const,
          title: i18n.t('accountingImport.status.ai_prediction_in_progress'),
          text: i18n.t('accountingImport.statusProcessingText'),
        }
      case AccountingImportStatus.DONE:
        if (noApplicationsDetected.value) {
          return {
            icon: SearchX,
            color: 'warning' as const,
            title: i18n.t('accountingImport.noApplicationsDetected'),
            text: i18n.t('accountingImport.noApplicationsDetected'),
          }
        }
        return null
      case AccountingImportStatus.ERROR:
        return {
          icon: AlertTriangle,
          color: 'error' as const,
          title: i18n.t('accountingImport.statusErrorTitle'),
          text: i18n.t('accountingImport.statusErrorText'),
        }
      default:
        if (noApplicationsDetected.value) {
          return {
            icon: SearchX,
            color: 'warning' as const,
            title: i18n.t('accountingImport.noApplicationsDetected'),
            text: i18n.t('accountingImport.noApplicationsDetected'),
          }
        }
        return null
    }
  })

  const emptyStateActions = computed(() => {
    if (noApplicationsDetected.value) {
      return {
        showCreateBtn: true,
        createLabel: i18n.t('delete'),
        showDocsBtn: true,
        docsLabel:
          accountingImport.value?.status === AccountingImportStatus.UPLOADED
            ? i18n.t('accountingExport.startProcessing')
            : i18n.t('accountingExport.reanalyze'),
      }
    }
    return {
      showCreateBtn: false,
      showDocsBtn: false,
      createLabel: '',
      docsLabel: '',
    }
  })

  const exportFilters = computed(() => {
    const softwareNameFilter = buildSoftwareNameRegex(searchTerm.value)
    const blacklistedParam =
      blacklistedValue.value === 'blacklisted'
        ? true
        : blacklistedValue.value === 'not_blacklisted'
          ? false
          : undefined

    return {
      softwareNamesRegex: softwareNameFilter?.regex,
      softwareNamesOptions: softwareNameFilter?.options,
      aiPredictionEq:
        aiPredictionEnabled.value && sourceValue.value !== 'all'
          ? sourceValue.value === 'ai'
          : undefined,
      predictionCertaintyGte:
        aiPredictionEnabled.value && thresholdActive.value
          ? (thresholdFilter.value ?? undefined)
          : undefined,
      blacklistedEq: blacklistedParam,
    }
  })

  // ════════════════════════════════════════════════════════════════════════════
  //  Lifecycle
  // ════════════════════════════════════════════════════════════════════════════
  onMounted(() => {
    initPage()
  })

  watch(accountingImportId, function handleImportIdChange() {
    initPage()
  })

  watch(targetCurrencyCode, function handleCurrencyChange() {
    if (initializing.value) return
    if (!accountingImport.value) return
    if (targetCurrencyCode.value === lastSummaryCurrency.value) return
    fetchSummaryDebounced()
  })

  async function initPage() {
    initializing.value = true
    await fetchImport()
    lastSummaryCurrency.value = targetCurrencyCode.value ?? null
    await fetchImportStatisticsSummary()
    loadAvailableTags()
    initializing.value = false
  }

  // ════════════════════════════════════════════════════════════════════════════
  //  Data fetching
  // ════════════════════════════════════════════════════════════════════════════

  function buildSoftwareNameRegex(term: string) {
    const value = term.trim()
    if (!value) return null
    const escaped = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    return {
      regex: `.*${escaped}.*`,
      options: 'i',
    }
  }

  async function fetchImport() {
    if (!accountingImportId.value) {
      return
    }

    loadingDetail.value = true
    try {
      const response =
        await AccountingService.getAccountingImportApiV1AccountingAccountingImportsAccountingImportIdGet(
          {
            accountingImportId: accountingImportId.value,
          }
        )
      accountingImport.value = response
    } catch (error) {
      console.error(error)
      sendToast(i18n.t('failedToLoadData'), undefined, 'error')
      accountingImport.value = null
    } finally {
      loadingDetail.value = false
    }
  }

  async function fetchImportStatisticsSummary() {
    if (!accountingImportId.value) return
    if (
      !targetCurrencyCode.value &&
      !preferenceStore.preferences?.currency_code
    )
      return
    rowsLoading.value = true
    delayedLoading.value = false
    if (loadingTimer) {
      window.clearTimeout(loadingTimer)
      loadingTimer = undefined
    }
    if (softwareSummary.value.length > 0) {
      loadingTimer = window.setTimeout(() => {
        delayedLoading.value = true
      }, 500)
    }
    try {
      const softwareNameFilter = buildSoftwareNameRegex(searchTerm.value)
      const blacklistedParam =
        blacklistedValue.value === 'blacklisted'
          ? true
          : blacklistedValue.value === 'not_blacklisted'
            ? false
            : undefined
      const [unfiltered, filtered] = await Promise.all([
        AccountingService.getAccountingImportStatisticsSummaryApiV1AccountingAccountingImportsAccountingImportIdStatisticsSummaryGet(
          {
            accountingImportId: accountingImportId.value,
            targetCurrencyCode: targetCurrencyCode.value ?? undefined,
          }
        ),
        AccountingService.getAccountingImportStatisticsSummaryApiV1AccountingAccountingImportsAccountingImportIdStatisticsSummaryGet(
          {
            accountingImportId: accountingImportId.value,
            softwareNamesRegex: softwareNameFilter?.regex,
            softwareNamesOptions: softwareNameFilter?.options,
            aiPredictionEq:
              sourceValue.value === 'all'
                ? undefined
                : sourceValue.value === 'ai',
            predictionCertaintyGte: thresholdActive.value
              ? (thresholdFilter.value ?? undefined)
              : undefined,
            blacklistedEq: blacklistedParam,
            targetCurrencyCode: targetCurrencyCode.value ?? undefined,
          }
        ),
      ])

      allSoftwareSummary.value = unfiltered.software_details ?? []
      softwareSummary.value = filtered.software_details ?? []
      totalSaasSpends.value = unfiltered.total_saas_spends ?? null
      lastSummaryCurrency.value = targetCurrencyCode.value ?? null
      totalRowsCount.value = softwareSummary.value.reduce(
        (sum, item) => sum + (item.booking_entries ?? 0),
        0
      )
      softwareDetails.value = {}
      applyFilters()
    } catch (error) {
      console.error(error)
      allSoftwareSummary.value = []
      softwareSummary.value = []
      totalSaasSpends.value = null
      totalRowsCount.value = 0
      filteredSummaryRows.value = []
    } finally {
      rowsLoading.value = false
      if (loadingTimer) {
        window.clearTimeout(loadingTimer)
        loadingTimer = undefined
      }
      delayedLoading.value = false
    }
  }

  async function loadAvailableTags() {
    try {
      const tags = await TagsService.getTagsApiV1ManagementTagsGet({})
      availableTags.value = tags
    } catch (error) {
      console.error(error)
    }
  }

  async function handleExpandRow(softwareName: string) {
    if (!accountingImportId.value) return
    const state = softwareDetails.value[softwareName]
    if (state?.loading || state?.loaded) return

    softwareDetails.value[softwareName] = {
      loading: true,
      loaded: false,
      rows: [],
    }

    try {
      const softwareNameFilter = buildSoftwareNameRegex(softwareName)
      const blacklistedParam =
        blacklistedValue.value === 'blacklisted'
          ? true
          : blacklistedValue.value === 'not_blacklisted'
            ? false
            : undefined

      const rows =
        await AccountingService.getAccountingImportRowsApiV1AccountingAccountingImportRowsGet(
          {
            accountingImportId: accountingImportId.value,
            softwareNamesRegex: softwareNameFilter?.regex,
            softwareNamesOptions: softwareNameFilter?.options,
            aiPredictionEq:
              sourceValue.value === 'all'
                ? undefined
                : sourceValue.value === 'ai',
            predictionCertaintyGte: thresholdActive.value
              ? (thresholdFilter.value ?? undefined)
              : undefined,
            blacklistedEq: blacklistedParam,
            targetCurrencyCode: targetCurrencyCode.value ?? undefined,
          }
        )

      softwareDetails.value[softwareName] = {
        loading: false,
        loaded: true,
        rows: rows ?? [],
      }
    } catch (error) {
      console.error(error)
      softwareDetails.value[softwareName] = {
        loading: false,
        loaded: false,
        rows: [],
      }
      sendToast(i18n.t('failedToLoadData'), undefined, 'error')
    }
  }

  // ════════════════════════════════════════════════════════════════════════════
  //  Actions
  // ════════════════════════════════════════════════════════════════════════════
  function handleApplicationEditButton() {
    if (!accountingImport.value) {
      sendToast(i18n.t('failedToLoadData'), undefined, 'error')
      return
    }

    selectedImport.value = accountingImport.value
    editDialogVisible.value = true
  }

  async function handleImportUpdated() {
    await fetchImport()
  }

  function openReanalyze() {
    if (!accountingImport.value?._id) return
    reanalyzeImportId.value = accountingImport.value._id
    reanalyzeDialogVisible.value = true
  }

  async function handleReanalyzeImported() {
    reanalyzeDialogVisible.value = false
    reanalyzeImportId.value = null
    await fetchImport()
    await fetchImportStatisticsSummary()
  }

  function handleEmptyStateCreate() {
    if (accountingImport.value?._id) {
      deleteImport(accountingImport.value._id)
    }
  }

  function handleEmptyStateShowDocs() {
    openReanalyze()
  }

  async function downloadImport(importRecord: AccountingImportOut) {
    try {
      const [serviceData, headerFilename] = await Promise.all([
        AccountingService.downloadAccountingImportFileApiV1AccountingAccountingImportsAccountingImportIdDownloadGet(
          {
            accountingImportId: importRecord._id,
          }
        ),
        getFilenameFromHead(importRecord._id),
      ])

      let blob: Blob | null = null
      let filename =
        headerFilename || importRecord.filename || 'accounting-import.csv'

      if (serviceData !== undefined) {
        blob = new Blob([serviceData as BlobPart], { type: 'text/csv' })
      } else {
        const fetched = await fetchImportBlob(importRecord._id)
        if (fetched) {
          blob = fetched.blob
          filename =
            extractFilename(fetched.contentDisposition) ||
            filename ||
            'accounting-import.csv'
        }
      }

      if (!blob) {
        throw new Error('No data received for download.')
      }

      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      console.error(error)
      sendToast(i18n.t('accountingExport.downloadError'), undefined, 'error')
    }
  }

  async function deleteImport(id: string) {
    try {
      await AccountingService.deleteAccountingImportApiV1AccountingAccountingImportsAccountingImportIdDelete(
        {
          accountingImportId: id,
        }
      )
      sendToast(i18n.t('accountingExport.deleteSuccess'), undefined, 'success')
      softwareSummary.value = []
      filteredSummaryRows.value = []
      totalRowsCount.value = 0
      totalSaasSpends.value = null
      accountingImport.value = null
      router.push({ name: 'Discovery', query: { tab: 'imports' } })
    } catch (error) {
      console.error(error)
      sendToast(i18n.t('accountingExport.deleteError'), undefined, 'error')
    }
  }

  function openExportDialog() {
    exportDialogVisible.value = true
  }

  function extractFilename(header: string | null): string | undefined {
    if (!header) return undefined
    const match = /filename\*?=(?:UTF-8''|"?)([^\";]+)/i.exec(header)
    if (match && match[1]) {
      try {
        return decodeURIComponent(match[1])
      } catch {
        return match[1]
      }
    }
    return undefined
  }

  async function getFilenameFromHead(importId: string) {
    try {
      const url = `${OpenAPI.BASE}/api/v1/accounting/accounting-imports/${encodeURIComponent(importId)}/download`
      const headResponse = await fetch(url, {
        method: 'HEAD',
        headers: {
          Authorization: `Bearer ${OpenAPI.TOKEN}`,
          Accept: 'text/csv,application/octet-stream',
        },
      })
      if (!headResponse.ok) return undefined
      const contentDisposition = headResponse.headers.get('content-disposition')
      return extractFilename(contentDisposition)
    } catch {
      return undefined
    }
  }

  async function fetchImportBlob(importId: string) {
    const url = `${OpenAPI.BASE}/api/v1/accounting/accounting-imports/${encodeURIComponent(importId)}/download`
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${OpenAPI.TOKEN}`,
        Accept: 'text/csv,application/octet-stream',
      },
    })
    if (!response.ok) return null
    const contentDisposition = response.headers.get('content-disposition')
    const blob = await response.blob()
    return { blob, contentDisposition }
  }
</script>

<style scoped lang="scss">
  .accounting-details-grid {
    display: grid;
    width: 100%;
    gap: var(--gap-lg);
    grid-template-columns: repeat(6, 1fr);
    grid-template-rows: auto minmax(0, 1fr);
    grid-template-areas:
      'headerCard headerCard headerCard headerCard headerCard headerCard'
      'detailCard detailCard detailCard detailCard detailCard detailCard';

    .header-card {
      grid-area: headerCard;
    }

    .detail-card {
      grid-area: detailCard;
      min-height: 0;
    }
  }
</style>
